import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  ServerStackIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/layout/Layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  SelectOption,
  Spinner,
  TabularDataViewer,
  Textarea,
  type TabularColumn,
} from '../../components/ui';
import { cn } from '../../shared/lib/cn';
import { useToasts } from '../../stores/useToasts';
import {
  downloadPayrollSourceFile,
  useClearPayrollDataSource,
  usePayrollAvailableConnectors,
  usePayrollDataSourceConfig,
  usePayrollRun,
  useSetPayrollDataSource,
  useTestPayrollConnection,
} from './api';
import type {
  AuditEntry,
  PayrollBurdenComponent,
  PayrollDataSource,
  PayrollEmployeeAccrual,
  PayrollHeadcountTieOutRow,
  PayrollJournalEntryRow,
  PayrollRun,
  PayrollSourceFile,
  PayrollStatus,
  PayrollTab,
} from './types';
import {
  buildNetSuiteCsv,
  calculateBalance,
  formatCurrency,
  formatPct,
  routeToTab,
  safePeriodFileToken,
  tabToPath,
} from './utils';

const PERIODS = ['Apr 2025', 'May 2025'];

const TAB_LABELS: Array<{ id: PayrollTab; label: string }> = [
  { id: 'review', label: 'Controller Review' },
  { id: 'journal-entry', label: 'Journal Entry' },
  { id: 'headcount', label: 'Headcount Tie-Out' },
  { id: 'burden', label: 'Burden Detail' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'source-files', label: 'Source Files' },
  { id: 'configuration', label: 'Configuration' },
];

type EditingCell = {
  rowId: number;
  field: 'debit' | 'credit';
} | null;

export default function PayrollEntryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const pushToast = useToasts(state => state.push);
  const activeTab = routeToTab(location.pathname);
  const [period, setPeriod] = useState('Apr 2025');
  const { data, isLoading, isError, refetch } = usePayrollRun(period);
  const [rows, setRows] = useState<PayrollJournalEntryRow[]>([]);
  const [status, setStatus] = useState<PayrollStatus>('pending');
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editValue, setEditValue] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!data) return;
    setRows(data.journal_entries);
    setStatus('pending');
    setAuditEntries([
      makeAuditEntry(
        'EC-04 Agent',
        `${data.kpis.period} payroll accrual generated with ${data.kpis.je_line_count} NetSuite JE lines.`,
      ),
    ]);
    setEditingCell(null);
    setRejectOpen(false);
    setRejectReason('');
  }, [data]);

  const balance = useMemo(() => calculateBalance(rows), [rows]);
  const modifiedCount = rows.filter(row => row.modified).length;

  const addAudit = (actor: string, action: string) => {
    setAuditEntries(current => [makeAuditEntry(actor, action), ...current]);
  };

  const selectTab = (tab: PayrollTab) => {
    navigate(tabToPath(tab));
  };

  const startEditing = (row: PayrollJournalEntryRow, field: 'debit' | 'credit') => {
    if (status !== 'pending') return;
    setEditingCell({ rowId: row.id, field });
    setEditValue(String(row[field] || ''));
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const nextValue = Number.parseFloat(editValue);
    if (Number.isNaN(nextValue) || nextValue < 0) {
      pushToast({ kind: 'error', message: 'Enter a valid non-negative amount.' });
      return;
    }

    setRows(current => current.map(row => {
      if (row.id !== editingCell.rowId) return row;
      const oldValue = row[editingCell.field];
      const updated = {
        ...row,
        [editingCell.field]: roundMoney(nextValue),
      };
      updated.modified =
        updated.debit !== data?.journal_entries.find(original => original.id === row.id)?.debit
        || updated.credit !== data?.journal_entries.find(original => original.id === row.id)?.credit;
      addAudit(
        'Controller',
        `Modified line ${row.line} ${editingCell.field} from ${formatCurrency(oldValue)} to ${formatCurrency(nextValue)}.`,
      );
      return updated;
    }));
    setEditingCell(null);
    setEditValue('');
  };

  const resetModifications = () => {
    if (!data) return;
    setRows(data.journal_entries);
    addAudit('Controller', 'Reset all JE modifications to the agent-generated values.');
    pushToast({ kind: 'info', message: 'JE modifications reset.' });
  };

  const approve = () => {
    if (!balance.isBalanced) {
      pushToast({ kind: 'error', message: 'JE must balance before approval.' });
      return;
    }
    if (!data) return;

    const csv = buildNetSuiteCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `EC04_NS_JE_${safePeriodFileToken(data.kpis.period)}_APPROVED.csv`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
    link.remove();

    setStatus('approved');
    addAudit(
      'Controller',
      `Approved JE and generated NetSuite CSV with ${modifiedCount} modified line(s).`,
    );
    pushToast({ kind: 'success', message: 'Approved. NetSuite CSV downloaded.' });
    selectTab('audit');
  };

  const reject = () => {
    const reason = rejectReason.trim();
    if (!reason) {
      pushToast({ kind: 'error', message: 'Rejection reason is required.' });
      return;
    }
    setStatus('rejected');
    setRejectOpen(false);
    addAudit('Controller', `Rejected JE. Reason: ${reason}`);
    pushToast({ kind: 'warning', message: 'JE rejected and audit log updated.' });
    selectTab('audit');
  };

  const handleDownloadSourceFile = async (file: PayrollSourceFile) => {
    try {
      await downloadPayrollSourceFile(file);
      addAudit('Controller', `Downloaded source artifact: ${file.filename}.`);
    } catch (error) {
      pushToast({ kind: 'error', message: 'Unable to download source file.' });
    }
  };

  return (
    <Layout>
      <div className="flex h-full flex-col bg-gray-50 dark:bg-dark-bg">
        <div
          className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface"
          data-toolbar-surface="solid"
        >
          <div className="mx-auto max-w-[1680px] px-6 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <Badge variant={statusVariant(status)}>{status}</Badge>
                <span className="font-mono text-xs uppercase tracking-wider text-teal-600 dark:text-teal-400">
                  EC-04
                </span>
                {data?.data_source && (
                  <Badge variant={data.data_source.is_connected ? 'success' : 'secondary'}>
                    {data.data_source.mode}
                  </Badge>
                )}
              </div>
              <h1 className="mt-2 font-title text-h1 text-charcoal dark:text-gray-100">
                Payroll Accrual
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
                Headcount tie-out, employer burden calculation, NetSuite JE, and Controller approval workflow.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod} className="w-40">
                {PERIODS.map(option => (
                  <SelectOption key={option} value={option}>{option}</SelectOption>
                ))}
              </Select>
              <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}

        {isError && (
          <div className="flex flex-1 items-center justify-center p-6">
            <Card className="max-w-lg">
              <CardContent className="p-6 text-center">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-amber-500" />
                <h2 className="mt-3 text-lg font-semibold text-charcoal dark:text-gray-100">
                  Payroll run unavailable
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  The applet could not load the packaged payroll workbooks.
                </p>
                <Button className="mt-4" variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {data && !isLoading && !isError && (
          <>
            <KpiStrip data={data} modifiedCount={modifiedCount} balanceDelta={balance.delta} />
            <div className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
              <div className="mx-auto max-w-[1680px] px-6">
              <div className="flex gap-1 overflow-x-auto py-2">
                {TAB_LABELS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => selectTab(tab.id)}
                    className={cn(
                      'whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      activeTab === tab.id
                        ? 'bg-eliza-red/10 text-eliza-red'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-charcoal dark:text-gray-400 dark:hover:bg-dark-surface-2 dark:hover:text-gray-100',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto pb-28">
              <div className="mx-auto max-w-[1680px] px-6 py-5">
              {activeTab === 'review' && (
                <ReviewPanel
                  data={data}
                  rows={rows}
                  status={status}
                  balance={balance}
                />
              )}
              {activeTab === 'journal-entry' && (
                <JournalEntryPanel
                  rows={rows}
                  originalRows={data.journal_entries}
                  status={status}
                  editingCell={editingCell}
                  editValue={editValue}
                  onEditValueChange={setEditValue}
                  onStartEditing={startEditing}
                  onCommitEdit={commitEdit}
                />
              )}
              {activeTab === 'headcount' && <HeadcountPanel data={data} />}
              {activeTab === 'burden' && <BurdenPanel data={data} />}
              {activeTab === 'audit' && (
                <AuditPanel
                  entries={auditEntries}
                  status={status}
                  modifiedCount={modifiedCount}
                  period={data.kpis.period}
                />
              )}
              {activeTab === 'source-files' && (
                <SourceFilesPanel
                  files={data.source_files}
                  onDownload={handleDownloadSourceFile}
                />
              )}
              {activeTab === 'configuration' && <ConfigurationPanel dataSource={data.data_source} />}
              </div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 px-6 py-3 backdrop-blur dark:border-dark-border dark:bg-dark-surface/95">
              <div className="ml-0 flex flex-wrap items-center justify-between gap-3 lg:ml-[280px]">
                <div className="flex items-center gap-4">
                  {balance.isBalanced ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                  )}
                  <div>
                    <p className="text-sm font-semibold text-charcoal dark:text-gray-100">
                      {balance.isBalanced ? 'JE is balanced' : 'JE is out of balance'}
                    </p>
                    <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      Debits {formatCurrency(balance.debitTotal)} / Credits {formatCurrency(balance.creditTotal)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" onClick={resetModifications} disabled={status !== 'pending' || modifiedCount === 0}>
                    <ArrowPathIcon className="h-4 w-4" />
                    Reset
                  </Button>
                  <Button variant="destructive" onClick={() => setRejectOpen(true)} disabled={status !== 'pending'}>
                    <XCircleIcon className="h-4 w-4" />
                    Reject
                  </Button>
                  <Button variant="brand" onClick={approve} disabled={status !== 'pending' || !balance.isBalanced}>
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    Approve & Generate CSV
                  </Button>
                </div>
              </div>
            </div>

            <Modal open={rejectOpen} onClose={() => setRejectOpen(false)}>
              <ModalContent size="lg">
                <ModalHeader>
                  <ModalTitle>Reject Payroll JE</ModalTitle>
                  <ModalDescription>
                    Record the rejection reason for Controller review evidence.
                  </ModalDescription>
                </ModalHeader>
                <ModalBody>
                  <Textarea
                    value={rejectReason}
                    onChange={event => setRejectReason(event.target.value)}
                    placeholder="Describe the issue that blocks approval."
                    rows={5}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondary" onClick={() => setRejectOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={reject}>
                    Confirm Rejection
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        )}
      </div>
    </Layout>
  );
}

function KpiStrip({ data, modifiedCount, balanceDelta }: { data: PayrollRun; modifiedCount: number; balanceDelta: number }) {
  const kpis = data.kpis;
  const metrics = [
    { label: 'Employees', value: kpis.employees.toLocaleString(), accent: false },
    { label: 'Gross Payroll', value: formatCurrency(kpis.gross_payroll, true), accent: true },
    { label: 'Employer Burden', value: `${formatCurrency(kpis.employer_burden, true)} (${formatPct(kpis.burden_pct)})`, accent: false },
    { label: 'Total JE', value: formatCurrency(kpis.total_je, true), accent: true },
    { label: 'Modified Lines', value: modifiedCount.toLocaleString(), accent: false },
    { label: 'Balance Delta', value: formatCurrency(balanceDelta), accent: Math.abs(balanceDelta) < 0.02 },
  ];

  return (
    <div className="grid border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface sm:grid-cols-2 lg:grid-cols-6">
      {metrics.map(metric => (
        <div key={metric.label} className="border-r border-gray-200 px-5 py-4 last:border-r-0 dark:border-dark-border">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{metric.label}</p>
          <p className={cn('mt-1 font-mono text-lg font-semibold', metric.accent ? 'text-teal-600 dark:text-teal-400' : 'text-charcoal dark:text-gray-100')}>
            {metric.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function ReviewPanel({ data, rows, status, balance }: { data: PayrollRun; rows: PayrollJournalEntryRow[]; status: PayrollStatus; balance: ReturnType<typeof calculateBalance> }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.6fr)]">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
                Controller Review Packet
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{data.agent.description}</p>
            </div>
            <Badge variant={statusVariant(status)}>{status}</Badge>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <SummaryTile label="Controller" value={data.agent.controller} />
            <SummaryTile label="HR Contact" value={data.agent.hr_contact} />
            <SummaryTile label="Client" value={data.agent.client} />
          </div>
          <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
            <div className="flex items-center gap-2">
              {balance.isBalanced ? (
                <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
              )}
              <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
                Balance Check
              </h3>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <SummaryTile label="Debits" value={formatCurrency(balance.debitTotal)} />
              <SummaryTile label="Credits" value={formatCurrency(balance.creditTotal)} />
              <SummaryTile label="Delta" value={formatCurrency(balance.delta)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">EC-04 Agent Review</h2>
            <Badge variant={data.agent_review.overall_status === 'ready_for_controller' ? 'success' : 'warning'}>
              {data.agent_review.overall_status.replace(/_/g, ' ')}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Prompt {data.agent_review.prompt_key} via {data.agent_review.prompt_source_backend}; Langfuse tracing {data.agent_review.traced_with_langfuse ? 'enabled' : 'configured no-op'}.
          </p>
          <div className="mt-4 space-y-3">
            {[...data.agent_review.observations, ...data.observations].map((observation: string) => (
              <div key={observation} className="flex gap-3 rounded-lg bg-gray-50 p-3 dark:bg-dark-surface-2">
                <ClipboardDocumentListIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal-600" />
                <p className="text-sm text-gray-600 dark:text-gray-300">{observation}</p>
              </div>
            ))}
          </div>
          {data.agent_review.risks.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
              {data.agent_review.risks[0]}
            </div>
          )}
          <div className="mt-4 rounded-lg border border-gray-200 p-3 dark:border-dark-border">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Run Contents</p>
            <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
              {rows.length} JE lines, {data.headcount_tie_out.length} department tie-outs, {data.employee_accruals.length} employee accrual records.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function JournalEntryPanel({
  rows,
  originalRows,
  status,
  editingCell,
  editValue,
  onEditValueChange,
  onStartEditing,
  onCommitEdit,
}: {
  rows: PayrollJournalEntryRow[];
  originalRows: PayrollJournalEntryRow[];
  status: PayrollStatus;
  editingCell: EditingCell;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onStartEditing: (row: PayrollJournalEntryRow, field: 'debit' | 'credit') => void;
  onCommitEdit: () => void;
}) {
  const columns: TabularColumn<PayrollJournalEntryRow>[] = [
    { id: 'line', header: 'Line', accessorKey: 'line', type: 'number', width: '80px' },
    { id: 'account', header: 'Account', accessorKey: 'account', width: '100px' },
    { id: 'account_name', header: 'Account Name', accessorKey: 'account_name', minWidth: '180px' },
    {
      id: 'debit',
      header: 'Debit',
      accessorKey: 'debit',
      type: 'currency',
      align: 'right',
      width: '140px',
      cell: ({ row }) => {
        const original = originalRows.find(item => item.id === row.id);
        return (
          <AmountCell
            row={row}
            field="debit"
            originalValue={original?.debit ?? row.debit}
            editingCell={editingCell}
            editValue={editValue}
            disabled={status !== 'pending' || row.debit <= 0}
            onEditValueChange={onEditValueChange}
            onStartEditing={onStartEditing}
            onCommitEdit={onCommitEdit}
          />
        );
      },
    },
    {
      id: 'credit',
      header: 'Credit',
      accessorKey: 'credit',
      type: 'currency',
      align: 'right',
      width: '140px',
      cell: ({ row }) => {
        const original = originalRows.find(item => item.id === row.id);
        return (
          <AmountCell
            row={row}
            field="credit"
            originalValue={original?.credit ?? row.credit}
            editingCell={editingCell}
            editValue={editValue}
            disabled={status !== 'pending' || row.credit <= 0}
            onEditValueChange={onEditValueChange}
            onStartEditing={onStartEditing}
            onCommitEdit={onCommitEdit}
          />
        );
      },
    },
    { id: 'department', header: 'Department', accessorKey: 'department', minWidth: '180px' },
    { id: 'line_memo', header: 'Memo', accessorKey: 'line_memo', minWidth: '260px' },
    {
      id: 'modified',
      header: 'Status',
      accessorKey: 'modified',
      width: '120px',
      cell: ({ row }) => (
        row.modified ? <Badge variant="warning">Modified</Badge> : <Badge variant="secondary">Original</Badge>
      ),
      csvValue: row => (row.modified ? 'Modified' : 'Original'),
    },
  ];

  return (
    <Card>
      <CardContent className="p-5">
        <TabularDataViewer
          title="NetSuite Journal Entry"
          description="Double-click debit or credit amounts to modify while the JE is pending."
          data={rows}
          columns={columns}
          getRowId={row => row.id}
          exportFilename="payroll-journal-entry.csv"
          maxHeight="640px"
          pageSize={30}
          compact
        />
      </CardContent>
    </Card>
  );
}

function AmountCell({
  row,
  field,
  originalValue,
  editingCell,
  editValue,
  disabled,
  onEditValueChange,
  onStartEditing,
  onCommitEdit,
}: {
  row: PayrollJournalEntryRow;
  field: 'debit' | 'credit';
  originalValue: number;
  editingCell: EditingCell;
  editValue: string;
  disabled: boolean;
  onEditValueChange: (value: string) => void;
  onStartEditing: (row: PayrollJournalEntryRow, field: 'debit' | 'credit') => void;
  onCommitEdit: () => void;
}) {
  const isEditing = editingCell?.rowId === row.id && editingCell.field === field;
  const changed = row[field] !== originalValue;

  return (
    <div
      className={cn('font-mono', !disabled && 'cursor-pointer')}
      onDoubleClick={() => !disabled && onStartEditing(row, field)}
    >
      {isEditing ? (
        <input
          autoFocus
          value={editValue}
          onChange={event => onEditValueChange(event.target.value)}
          onBlur={onCommitEdit}
          onKeyDown={event => {
            if (event.key === 'Enter') onCommitEdit();
          }}
          className="w-28 rounded-md border border-gray-300 bg-white px-2 py-1 text-right text-sm text-charcoal outline-none focus:border-eliza-red dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
        />
      ) : (
        <span className={cn(changed && 'text-amber-600 dark:text-amber-400')}>
          {row[field] ? formatCurrency(row[field]) : ''}
        </span>
      )}
    </div>
  );
}

function HeadcountPanel({ data }: { data: PayrollRun }) {
  const columns: TabularColumn<PayrollHeadcountTieOutRow>[] = [
    { id: 'department', header: 'Department', accessorKey: 'department', minWidth: '180px' },
    { id: 'hris_hc', header: 'HRIS HC', accessorKey: 'hris_hc', type: 'number', align: 'right' },
    { id: 'payroll_hc', header: 'Payroll HC', accessorKey: 'payroll_hc', type: 'number', align: 'right' },
    { id: 'summary_hc', header: 'Summary HC', accessorKey: 'summary_hc', type: 'number', align: 'right' },
    { id: 'hris_fully_loaded', header: 'HRIS Loaded', accessorKey: 'hris_fully_loaded', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    { id: 'summary_fully_loaded', header: 'Summary Loaded', accessorKey: 'summary_fully_loaded', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    {
      id: 'variance',
      header: 'Variance',
      accessorKey: 'variance',
      type: 'currency',
      align: 'right',
      cell: ({ row }) => (
        <span className={row.variance < 0 ? 'text-red-600' : 'text-emerald-600'}>
          {formatCurrency(row.variance)}
        </span>
      ),
    },
    { id: 'variance_pct', header: 'Variance %', accessorKey: 'variance_pct', type: 'percent', align: 'right', cell: ({ value }) => formatPct(value) },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => <Badge variant={tieOutVariant(row.status)}>{row.status}</Badge>,
    },
  ];

  return (
    <Card>
      <CardContent className="p-5">
        <TabularDataViewer
          title="Three-Way Headcount Tie-Out"
          description="HRIS employee master, payroll register, and headcount summary by department."
          data={data.headcount_tie_out}
          columns={columns}
          getRowId={row => row.department}
          exportFilename="payroll-headcount-tie-out.csv"
          maxHeight="640px"
          compact
        />
      </CardContent>
    </Card>
  );
}

function BurdenPanel({ data }: { data: PayrollRun }) {
  const columns: TabularColumn<PayrollEmployeeAccrual>[] = [
    { id: 'name', header: 'Employee', accessorKey: 'name', minWidth: '180px' },
    { id: 'department', header: 'Department', accessorKey: 'department', minWidth: '180px' },
    { id: 'gross', header: 'Gross', accessorKey: 'gross', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    { id: 'employer_fica', header: 'FICA', accessorKey: 'employer_fica', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    { id: 'employer_401k', header: '401k', accessorKey: 'employer_401k', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    { id: 'healthcare', header: 'Healthcare', accessorKey: 'healthcare', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    {
      id: 'other_taxes',
      header: 'Other Taxes',
      type: 'currency',
      align: 'right',
      accessorFn: row => row.payroll_taxes_other + row.workers_comp,
      cell: ({ row }) => formatCurrency(row.payroll_taxes_other + row.workers_comp),
      csvValue: row => row.payroll_taxes_other + row.workers_comp,
    },
    { id: 'employer_burden', header: 'Burden', accessorKey: 'employer_burden', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
    { id: 'pto_accrual', header: 'PTO', accessorKey: 'pto_accrual', type: 'currency', align: 'right', cell: ({ value }) => formatCurrency(value) },
  ];

  return (
    <div className="grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
      <Card>
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">Burden Components</h2>
          <div className="mt-4 space-y-3">
            {data.burden_components.map((component: PayrollBurdenComponent) => (
              <div key={component.key} className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-charcoal dark:text-gray-100">{component.label}</p>
                  <p className="font-mono text-sm text-charcoal dark:text-gray-100">{formatCurrency(component.amount)}</p>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formatPct(component.pct_of_gross)} of gross / DR {component.debit_account} / CR {component.credit_account}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-5">
          <TabularDataViewer
            title="Employee Accrual Detail"
            data={data.employee_accruals}
            columns={columns}
            getRowId={row => row.emp_id}
            exportFilename="payroll-employee-accruals.csv"
            maxHeight="640px"
            pageSize={20}
            compact
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AuditPanel({ entries, status, modifiedCount, period }: { entries: AuditEntry[]; status: PayrollStatus; modifiedCount: number; period: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">Audit Log</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              SOX evidence for Controller review actions in this browser session.
            </p>
          </div>
          <Badge variant={statusVariant(status)}>{status}</Badge>
        </div>
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
          <p className="text-sm text-charcoal dark:text-gray-100">
            Certification: {period} payroll accrual is {status}. {modifiedCount} line(s) modified during review.
          </p>
        </div>
        <div className="mt-4 space-y-3">
          {entries.map(entry => (
            <div key={entry.id} className="flex flex-wrap gap-3 rounded-lg border border-gray-200 p-3 dark:border-dark-border">
              <span className="font-mono text-xs text-gray-500">{entry.timestamp}</span>
              <span className="text-sm font-medium text-teal-600 dark:text-teal-400">{entry.actor}</span>
              <span className="text-sm text-gray-600 dark:text-gray-300">{entry.action}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SourceFilesPanel({ files, onDownload }: { files: PayrollSourceFile[]; onDownload: (file: PayrollSourceFile) => void }) {
  const columns: TabularColumn<PayrollSourceFile>[] = [
    { id: 'label', header: 'Artifact', accessorKey: 'label', minWidth: '200px' },
    { id: 'filename', header: 'Filename', accessorKey: 'filename', minWidth: '280px' },
    { id: 'kind', header: 'Kind', accessorKey: 'kind', width: '100px', cell: ({ value }) => <Badge variant="secondary">{value}</Badge> },
    { id: 'size_bytes', header: 'Size', accessorKey: 'size_bytes', width: '110px', align: 'right', cell: ({ value }) => formatFileSize(value), csvValue: row => row.size_bytes },
    { id: 'description', header: 'Description', accessorKey: 'description', minWidth: '320px' },
    {
      id: 'download',
      header: '',
      width: '120px',
      sortable: false,
      cell: ({ row }) => (
        <Button type="button" size="sm" variant="secondary" onClick={() => onDownload(row)}>
          <ArrowDownTrayIcon className="h-4 w-4" />
          Download
        </Button>
      ),
      csvValue: () => '',
    } as TabularColumn<PayrollSourceFile>,
  ];

  return (
    <Card>
      <CardContent className="p-5">
        <TabularDataViewer
          title="Source Files"
          description="Original packaged workbooks, project instructions, skill definition, and sample outputs."
          data={files}
          columns={columns}
          getRowId={row => row.id}
          exportFilename="payroll-source-files.csv"
          compact
        />
      </CardContent>
    </Card>
  );
}

function ConfigurationPanel({ dataSource }: { dataSource: PayrollDataSource }) {
  const { data: config } = usePayrollDataSourceConfig();
  const { data: connectors = [], isLoading: connectorsLoading } = usePayrollAvailableConnectors();
  const setDataSource = useSetPayrollDataSource();
  const clearDataSource = useClearPayrollDataSource();
  const testConnection = useTestPayrollConnection();
  const [connectorId, setConnectorId] = useState('');
  const [schema, setSchema] = useState('payroll_entry');

  useEffect(() => {
    const activeConfig = config ?? dataSource;
    setConnectorId(activeConfig.connector_id ?? '');
    setSchema(activeConfig.redshift_schema ?? 'payroll_entry');
  }, [config, dataSource]);

  const activeConnector = connectors.find(connector => connector.connector_id === connectorId);
  const effectiveConfig = config ?? dataSource;

  const save = () => {
    if (!connectorId) return;
    setDataSource.mutate({ connector_id: connectorId, redshift_schema: schema || 'payroll_entry' });
  };

  const test = () => {
    if (!connectorId) return;
    testConnection.mutate({ connector_id: connectorId, redshift_schema: schema || 'payroll_entry' });
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <ServerStackIcon className="mt-1 h-5 w-5 text-teal-600" />
            <div>
              <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
                Data Source
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Select the tenant Redshift connector and schema that contain the payroll_* tables.
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div>
              <Label>Redshift Connector</Label>
              <Select
                value={connectorId}
                onValueChange={setConnectorId}
                placeholder={connectorsLoading ? 'Loading connectors' : 'Select a connector'}
                className="mt-1"
              >
                <SelectOption value="">Select a connector</SelectOption>
                {connectors.map(connector => (
                  <SelectOption key={connector.connector_id} value={connector.connector_id}>
                    {connector.connector_name}
                  </SelectOption>
                ))}
              </Select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {activeConnector?.connector_id ?? effectiveConfig.connector_name ?? 'Create a Redshift connector in Data Connections first.'}
              </p>
            </div>

            <div>
              <Label htmlFor="payroll-redshift-schema">Redshift Schema</Label>
              <Input
                id="payroll-redshift-schema"
                value={schema}
                onChange={event => setSchema(event.target.value)}
                placeholder="payroll_entry"
                className="mt-1"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Tables: payroll_employee_master, payroll_register, payroll_headcount_summary
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Button type="button" variant="brand" onClick={save} disabled={!connectorId || setDataSource.isPending}>
              {setDataSource.isPending ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button type="button" variant="secondary" onClick={test} disabled={!connectorId || testConnection.isPending}>
              {testConnection.isPending ? 'Testing...' : 'Test Connection'}
            </Button>
            {effectiveConfig.mode === 'saved' && (
              <Button type="button" variant="secondary" onClick={() => clearDataSource.mutate()} disabled={clearDataSource.isPending}>
                {clearDataSource.isPending ? 'Clearing...' : 'Use Fixture Fallback'}
              </Button>
            )}
          </div>

          {setDataSource.isSuccess && (
            <p className="mt-3 text-sm text-emerald-600">Configuration saved.</p>
          )}
          {setDataSource.isError && (
            <p className="mt-3 text-sm text-red-600">{setDataSource.error.message || 'Save failed.'}</p>
          )}
          {testConnection.data && (
            <div
              className={cn(
                'mt-3 rounded-md border p-3 text-sm',
                testConnection.data.redshift_connected
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-red-200 bg-red-50 text-red-800',
              )}
            >
              <strong>{testConnection.data.redshift_connected ? 'Connected' : 'Failed'}:</strong>{' '}
              {testConnection.data.message}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
            Active Source
          </h2>
          <div className="mt-4 space-y-3 text-sm">
            <SummaryTile label="Mode" value={<Badge variant={effectiveConfig.is_connected ? 'success' : 'secondary'}>{effectiveConfig.mode}</Badge>} />
            <SummaryTile label="Connector" value={effectiveConfig.connector_name || activeConnector?.connector_name || 'Fixture fallback'} />
            <SummaryTile label="Schema" value={effectiveConfig.redshift_schema || schema} />
            <SummaryTile label="Table Prefix" value={effectiveConfig.table_prefix || 'payroll_*'} />
          </div>
          {effectiveConfig.host && (
            <p className="mt-4 break-all font-mono text-xs text-gray-500 dark:text-gray-400">
              {effectiveConfig.host}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
      <div className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  );
}

function makeAuditEntry(actor: string, action: string): AuditEntry {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    timestamp: new Date().toLocaleString('en-US'),
    actor,
    action,
  };
}

function statusVariant(status: PayrollStatus): 'warning' | 'success' | 'danger' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'danger';
  return 'warning';
}

function tieOutVariant(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'Match') return 'success';
  if (status === 'Investigate') return 'danger';
  return 'warning';
}

function formatFileSize(size: number): string {
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
